import React from 'react'
import { OrderDirections } from '@store/graphql/__generated__/schema'
import {
  ActionButton,
  ActionGroup,
  Icon,
  Menu,
  MenuItem,
  OverlayTrigger,
  Popover,
  Theme
} from 'ui'
import {
  CurrentUserProjectVisibilityType,
  ProjectFilter,
  ProjectListType
} from './ProjectList'
import SortIcon from '/public/s2_icon_sort.svg'
import styles from '@styles/components/ProjectList.module.scss'
import { SpIconChevronDown } from 'workflow-icons'
import GlobeGridIcon from '/public/s2_icon_globe_grid.svg'
import SettingsActionButton from './SettingsActionButton'
import { useTranslations } from 'use-intl'

interface ProjectListFiltersProps {
  currentUserProjectVisibility: CurrentUserProjectVisibilityType
  handleOrderDirectionChange: ({ target: { value } }: any) => void
  setIsMenuOpen: React.Dispatch<React.SetStateAction<boolean>>
  isMenuOpen: boolean
  onCurrentUserProjectVisibilityChange: (e: unknown) => void
  onProjectFilterSelectionChange: (e: unknown) => void
  orderDirection: OrderDirections
  projectListType: ProjectListType
  selectableProjectFilters: ProjectFilter[]
  selectedProjectFilter: ProjectFilter
}

const ProjectListFilters = ({
  currentUserProjectVisibility,
  handleOrderDirectionChange,
  isMenuOpen,
  onCurrentUserProjectVisibilityChange,
  onProjectFilterSelectionChange,
  orderDirection,
  projectListType,
  selectableProjectFilters,
  selectedProjectFilter,
  setIsMenuOpen
}: ProjectListFiltersProps) => {
  const t = useTranslations()

  return (
    <div
      style={{
        paddingBottom:
          projectListType === ProjectListType.CurrentUser ? 24 : 'auto'
      }}
      className={styles['filters']}>
      <div className={styles['desktop-only']}>
        <div className={styles['filter-buttons']}>
          {projectListType !== ProjectListType.CurrentUser ? (
            <ActionGroup
              data-testid="project-filter-action-group"
              selects="single"
              selected={selectedProjectFilter as unknown as string[]}
              change={onProjectFilterSelectionChange}>
              {selectableProjectFilters.map(type => (
                <ActionButton
                  key={`project-filter-selector-${type}`}
                  className="action-btn-custom"
                  value={type}>
                  {t(`dashboard:projectList:filter:${type}`)}
                </ActionButton>
              ))}
            </ActionGroup>
          ) : (
            // The current user view no longer has selectable filters - always 'recent'
            // Instead, the filters can be used to toggle between private and public projects
            <ActionGroup
              data-testid="project-filter-action-group"
              selects="single"
              selected={currentUserProjectVisibility as unknown as string[]}
              change={onCurrentUserProjectVisibilityChange}>
              {[
                CurrentUserProjectVisibilityType.All,
                CurrentUserProjectVisibilityType.Shared
              ].map(visibility => (
                <ActionButton
                  key={`current-user-view-${visibility}`}
                  className="action-btn-custom"
                  value={visibility}
                  aria-label={visibility}>
                  {visibility === CurrentUserProjectVisibilityType.All ? (
                    t('dashboard:projectList:visibility:All')
                  ) : (
                    <>
                      <Icon slot="icon">
                        <GlobeGridIcon />
                      </Icon>
                      {t('dashboard:projectList:visibility:Shared')}
                    </>
                  )}
                </ActionButton>
              ))}
            </ActionGroup>
          )}
        </div>
      </div>
      <div className={styles['mobile-only']}>
        <Theme system="express" color="light">
          <OverlayTrigger
            id="trigger"
            placement="bottom-end"
            offset={6}
            open={isMenuOpen}
            spClosed={() => setIsMenuOpen(false)}>
            <ActionButton
              slot="trigger"
              className="action-btn-custom"
              aria-label="Open filters"
              onClick={() => setIsMenuOpen(true)}>
              <div className={styles['mobile-filter-picker-trigger']}>
                {projectListType !== ProjectListType.CurrentUser
                  ? selectedProjectFilter
                  : currentUserProjectVisibility}
                <Icon slot="icon">
                  <SpIconChevronDown />
                </Icon>
              </div>
            </ActionButton>
            <Popover slot="click-content" className={styles['mobile-popover']}>
              {projectListType !== ProjectListType.CurrentUser ? (
                <Menu
                  key="mobile-filter-select"
                  selects="single"
                  value={selectedProjectFilter}
                  onchange={onProjectFilterSelectionChange}>
                  {selectableProjectFilters.map((type: ProjectFilter) => (
                    <MenuItem
                      key={`mobile-filter-select-item-${type}`}
                      value={type}
                      selected={selectedProjectFilter === type}>
                      {t(`dashboard:projectList:filter:${type}`)}
                    </MenuItem>
                  ))}
                </Menu>
              ) : (
                <Menu
                  key="mobile-filter-select-currentuser"
                  selects="single"
                  value={currentUserProjectVisibility}
                  onchange={onCurrentUserProjectVisibilityChange}>
                  {[
                    CurrentUserProjectVisibilityType.All,
                    CurrentUserProjectVisibilityType.Shared
                  ].map(visibility => (
                    <MenuItem
                      key={`mobile-filter-select-currentuser-item-${visibility}`}
                      selected={currentUserProjectVisibility === visibility}
                      value={visibility}>
                      {t(`dashboard:projectList:visibility:${visibility}`)}
                    </MenuItem>
                  ))}
                </Menu>
              )}
            </Popover>
          </OverlayTrigger>
        </Theme>
      </div>
      <Theme system="express" color="light">
        <OverlayTrigger id="trigger" placement="bottom-end" offset={6}>
          <ActionButton slot="trigger">
            <Icon slot="icon">
              <SortIcon />
            </Icon>
          </ActionButton>
          <Popover slot="click-content">
            <Menu
              selects="single"
              value={orderDirection}
              onchange={handleOrderDirectionChange}>
              <MenuItem
                value={OrderDirections.Desc}
                selected={orderDirection === OrderDirections.Desc}>
                {t(`dashboard:projectList:sort:${selectedProjectFilter}:desc`)}
              </MenuItem>
              <MenuItem
                value={OrderDirections.Asc}
                selected={orderDirection === OrderDirections.Asc}>
                {t(`dashboard:projectList:sort:${selectedProjectFilter}:asc`)}
              </MenuItem>
            </Menu>
          </Popover>
        </OverlayTrigger>
      </Theme>

      {projectListType === ProjectListType.CurrentUser && (
        <SettingsActionButton />
      )}
    </div>
  )
}

export default ProjectListFilters
